import { LatLng, OsmTimezoneResponse1 } from '../../types';
import { delay, isDefined } from '../utils';
import { logWithAmplitude, throwHttpError } from './api';

import { HEADERS } from '../constants';
import { getCookie } from '../device';

export let GOOGLE_KEY = getCookie('GOOGLE_KEY') as string;

if (GOOGLE_KEY === undefined) {
    GOOGLE_KEY = HEADERS['X-Google-Key'];
}

// Only to be called on paged like discover that don't have a live map on them but need some other kind of google service like autocompelte
let googleMapsLoadPromise: Promise<void> | null = null;
const GEO_CACHE_LIMIT = 100;
const TZ_CACHE_LIMIT = 200;
const TZ_PRECISION = 4;

export async function loadGoogleMapsApi(libraries?: string) {
    if (googleMapsLoadPromise) {
        return await googleMapsLoadPromise;
    }
    if (typeof window !== 'undefined') {
        const globalWindow = window as typeof window & { google?: { maps?: unknown } };
        if (globalWindow.google?.maps !== undefined) {
            return;
        }
    }
    googleMapsLoadPromise = new Promise((resolve, reject) => {
        const scriptId = 'google-maps-api';
        // Check for any existing Google Maps script tags (including ones from Polymer components)
        const existingGoogleScript = Array.from(document.querySelectorAll('script')).find(
            script => script.src.includes('maps.googleapis.com/maps/api/js')
        );
        if (existingGoogleScript) {
            // Script is already loading or loaded - wait for it
            if (isDefined(window.google?.maps)) {
                resolve();
                return;
            }
            existingGoogleScript.addEventListener('load', () => resolve(), { once: true });
            existingGoogleScript.addEventListener('error', () => {
                googleMapsLoadPromise = null;
                reject(new Error('Google Maps script failed to load'));
            }, { once: true });
            return;
        }
        const script = document.createElement('script');
        const library_string = libraries ? `,${libraries}` : '';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&libraries=places${library_string}`;
        script.async = true;
        script.defer = true;
        script.type = 'text/javascript';
        script.id = scriptId;
        script.onload = () => {
            script.setAttribute('loaded', 'true');
            resolve();
        };
        script.onerror = () => {
            googleMapsLoadPromise = null;
            reject(new Error('Google Maps script failed to load'));
        };
        document.body.appendChild(script);
    });
    return await googleMapsLoadPromise;
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class MarkerService {
    static AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement | null = null;

    static async loadMarkerElement() {
        if (!this.AdvancedMarkerElement) {
            const { AdvancedMarkerElement } = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;
            this.AdvancedMarkerElement = AdvancedMarkerElement;
        }
    }
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class GeocoderService {
    static GeocoderElement: typeof google.maps.Geocoder | null = null;

    static async loadGeocoder() {
        if (!this.GeocoderElement) {
            // load script with library=
            const {Geocoder} = await google.maps.importLibrary('geocoding') as google.maps.GeocodingLibrary;
            this.GeocoderElement = Geocoder;
        }
    }
}

export interface PlacePredictionLite {
    description: string;
    place_id: string;
    main_text: string;
    types: string[];
}

// Places (New) autocomplete, returning predictions in the legacy
// AutocompletePrediction field shape our components consume.
export async function fetchPlacePredictionsAsync(request: {
    input: string;
    sessionToken?: google.maps.places.AutocompleteSessionToken;
    locationBias?: google.maps.LatLngBounds;
    types?: string[];
}): Promise<PlacePredictionLite[]> {
    try {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: request.input,
            sessionToken: request.sessionToken,
            locationBias: request.locationBias,
            includedPrimaryTypes: request.types && request.types.length > 0 ? request.types : undefined,
        });
        return suggestions
            .map(suggestion => suggestion.placePrediction)
            .filter(isDefined)
            .map(prediction => ({
                description: prediction.text.text,
                place_id: prediction.placeId,
                main_text: prediction.mainText?.text ?? prediction.text.text,
                types: prediction.types,
            }));
    } catch (error) {
        // Loud on purpose: a persistent failure here usually means a key/API
        // configuration problem, not a transient network blip.
        console.error('Place predictions request failed:', error);
        return [];
    }
}

const geocodeCache: Map<string, Promise<google.maps.GeocoderResult[]>> = new Map();
let sharedGeocoder: google.maps.Geocoder | null = null;

export async function geocodePlaceIdAsync(placeId: string): Promise<google.maps.GeocoderResult[]> {
    const cached = geocodeCache.get(placeId);
    if (cached !== undefined) {
        return await cached;
    }
    if (!sharedGeocoder) {
        sharedGeocoder = new google.maps.Geocoder();
    }
    const promise = sharedGeocoder
        .geocode({ placeId })
        .then((response) => response.results)
        .catch((error) => {
            geocodeCache.delete(placeId);
            throw error;
        });
    geocodeCache.set(placeId, promise);
    if (geocodeCache.size > GEO_CACHE_LIMIT) {
        const firstKey = geocodeCache.keys().next().value;
        geocodeCache.delete(firstKey);
    }
    return await promise;
}

// Both providers normalize through `OsmTimezoneResponse1[]` so callers
// can rely on `[0]?.identifier` regardless of which fallback served the
// request. The Google response shape is different from Twitchax's, so
// `timezoneAsyncGoogle` reshapes before returning.
const timezoneCache: Map<string, Promise<OsmTimezoneResponse1[]>> = new Map();

function normalizeCoord(value: number | string): number {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return Number(num.toFixed(TZ_PRECISION));
}

export async function timezoneAsync(lat: number | string, lng: number | string): Promise<OsmTimezoneResponse1[]> {
    // try timezoneAsyncTwitchax if error, then try timezoneAsyncGoogle
    // adding this in because of occasional errors with the twitchax timezone api
    const key = `${normalizeCoord(lat)},${normalizeCoord(lng)}`;
    const cached = timezoneCache.get(key);
    if (cached !== undefined) {
        return await cached;
    }
    const resultPromise = (async () => {
        try {
            const twitchax = await timezoneAsyncTwitchax(lat, lng);
            // Twitchax has occasionally returned non-array bodies on
            // partial outages; treat that as a failure so the Google
            // fallback runs instead of a malformed result poisoning
            // the cache.
            if (!Array.isArray(twitchax) || twitchax.length === 0 || !twitchax[0]?.identifier) {
                throw new Error('Twitchax timezone response missing identifier');
            }
            return twitchax;
        } catch (error) {
            return await timezoneAsyncGoogle(lat, lng);
        }
    })();
    const handledPromise = resultPromise.catch((error) => {
        // Remove failed lookups from the cache so future calls can retry
        timezoneCache.delete(key);
        throw error;
    });
    timezoneCache.set(key, handledPromise);
    if (timezoneCache.size > TZ_CACHE_LIMIT) {
        const firstKey = timezoneCache.keys().next().value;
        timezoneCache.delete(firstKey);
    }
    return await handledPromise;
}

export async function timezoneAsyncTwitchax(lat: number | string, lng: number | string): Promise<OsmTimezoneResponse1[]> {
    const response = await fetch(
        `https://tz.twitchax.com/api/v1/osm/tz/${lng}/${lat}`
    );
    const obj = await response.json();

    return obj as OsmTimezoneResponse1[];
}

interface GoogleTimezoneApiResponse {
    status?: string;
    timeZoneId?: string;
    timeZoneName?: string;
    rawOffset?: number;
    dstOffset?: number;
}

/**
 * Build a short tz abbreviation ("PDT", "EDT", etc.) from an IANA
 * identifier. Used to fill `shortIdentifier` for the Google fallback,
 * since Google's `timeZoneName` is the long form ("Pacific Daylight
 * Time"). Falls back to the IANA name if Intl can't produce a short
 * label.
 */
function shortTzNameFromIana(iana: string): string {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: iana,
            timeZoneName: 'short',
        }).formatToParts(new Date());
        return parts.find((p) => p.type === 'timeZoneName')?.value ?? iana;
    } catch {
        return iana;
    }
}

export async function timezoneAsyncGoogle(lat: number | string, lng: number | string): Promise<OsmTimezoneResponse1[]> {
    const KEY_TIMEZONE = 'AIzaSyAWtvkhhv4AyLCoy_qMg7XjyF8iJ4rsBPE';
    // Google's `timestamp` parameter is Unix seconds (used to pick the
    // right DST offset). The previous `new Date().getSeconds()` sent
    // 0-59, which Google interprets as Jan 1 1970 — same tz id but
    // wrong DST math. Pass real "now" seconds.
    const nowSeconds = Math.floor(Date.now() / 1000);
    const response = await fetch(
        `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${nowSeconds}&key=${KEY_TIMEZONE}`
    );
    const obj: GoogleTimezoneApiResponse = await response.json();

    if (obj.status !== 'OK' || obj.timeZoneId === undefined) {
        throw new Error(`Google timezone API failed: ${obj.status ?? 'unknown'}`);
    }

    // Reshape into the OsmTimezoneResponse1 contract every caller in the
    // codebase expects. Combining rawOffset + dstOffset gives the
    // currently-effective UTC offset, matching how the Twitchax response
    // populates `rawOffset`.
    const rawBase = obj.rawOffset ?? 0;
    const rawDst = obj.dstOffset ?? 0;
    return [{
        id: 0,
        identifier: obj.timeZoneId,
        shortIdentifier: shortTzNameFromIana(obj.timeZoneId),
        rawOffset: (rawBase + rawDst) * 1000,
        rawBaseOffset: rawBase * 1000,
        rawDstOffset: rawDst * 1000,
        zone: 0,
        currentTime: '',
    }];
}

export async function getGoogleProfile(amplitude_callsite: string, token: string) {
    const response = await fetch(
        `https://www.googleapis.com/oauth2/v3/userinfo?alt=json&access_token="${token}"`
    );

    if (!response.ok) {
        await throwHttpError('An error occurred while retrieving the google profile.', response);
    }

    const profile = await response.json();

    logWithAmplitude(amplitude_callsite, 'Get Google Profile', {
        email: profile.email,
        full_name: profile.name,
    });
    return profile;
}

export async function overridePaperInputPlaceChanged(locationElement: any) {
    while (!isDefined(locationElement) || !isDefined(locationElement._places) || !isDefined(google.maps.event)) {
        await delay(10);
    }
    
    locationElement._places.setFields(['name', 'place_id']);
    
    google.maps.event.clearListeners(locationElement._places, 'place_changed');

    google.maps.event.addListener(locationElement._places, 'place_changed', async () => {
        const place = locationElement._places.getPlace();
        const geocode = (await geocodePlaceIdAsync(place.place_id))[0];

        place.formatted_address = geocode.formatted_address;
        place.geometry = geocode.geometry;
        place.address_components = geocode.address_components;

        const p = locationElement._extractPlaceInfo(place, locationElement.$.nativeInput.value);
        locationElement._setPlace(p);
        locationElement._invalid = false;
        locationElement._setInvalid(false);
        locationElement._setLatLng({
            lat: p.latLng.lat,
            lng: p.latLng.lng
        });
        locationElement._value = locationElement.$.nativeInput.value;
        locationElement.value = {
            search: locationElement.$.nativeInput.value,
            place_id: p.place_id,
            latLng: {
                lat: p.latLng.lat,
                lng: p.latLng.lng
            }
        };
    });
}

export async function isLocationInputLatLong(locationString: string): Promise<LatLng | false>{
    const splits = locationString.split(',');
    if (splits.length === 2 && splits[0].trim().length <= 50) {
        const lat = Number(splits[0].trim());
        const lng = Number(splits[1].trim());
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            return {
                lat,
                lng
            };
        }
    }
    return false;
}

// the smooth zoom function
export function smoothZoom(map: google.maps.Map, max: number, cnt: number) {
    if (cnt < max) {
        setTimeout(() => {
            map.setZoom(cnt);
            smoothZoom(map, max, cnt + 1);
        }, 200);
    }
}

export async function smoothViewport(map: google.maps.Map, targetBounds: google.maps.LatLngBounds): Promise<void> {
    return await new Promise((resolve) => {
        // Calculate target zoom level
        const mapDim = map.getDiv().getBoundingClientRect();
        const targetZoom = getBoundsZoomLevel(targetBounds, { height: mapDim.height, width: mapDim.width });

        const targetCenter = targetBounds.getCenter();

        let lastZoom: number | undefined;

        // Gradually adjust the zoom level
        const zoomInterval = setInterval(async () => {
            const currentBounds = map.getBounds();
            const currentZoom = map.getZoom();

            if (currentBounds && currentZoom !== undefined) {
                // Check if current bounds contain target bounds
                const currentContainsTarget = currentBounds.contains(targetBounds.getNorthEast()) && currentBounds.contains(targetBounds.getSouthWest());

                if (!currentContainsTarget) {
                    // Zoom out if target bounds are not contained in current bounds
                    map.setZoom(currentZoom - 1);
                } else if (currentContainsTarget) {
                    map.panTo(targetCenter);
                    if (currentZoom < targetZoom) {
                        map.setZoom(currentZoom + 1);
                    } else {
                        clearInterval(zoomInterval);
                        resolve();
                    }

                    if (lastZoom === currentZoom) {
                        // Zoom level hasn't changed, break out of loop
                        clearInterval(zoomInterval);
                        resolve();
                    } else {
                        // Update last zoom level
                        lastZoom = currentZoom;
                    }
                }
            }
        }, 200);
    });
}

interface MapDimension {
    height: number;
    width: number;
}

export function getBoundsZoomLevel(bounds: google.maps.LatLngBounds, mapDim: MapDimension): number {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat: number): number {
        const sin = Math.sin(lat * Math.PI / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx: number, worldPx: number, fraction: number): number {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
}
